import * as React from 'react';
import {observer} from 'mobx-react';
import {action} from 'mobx';
import ReactTable, {CellInfo, Column, RowInfo} from 'react-table';
import {Input} from 'reactstrap';
import * as iso from 'iso3166-1';

import {Permissions} from '@app/AppConstants';

import LicenseBlockStore from '@app/Pages/PersonInfo/Stores/LicenseBlockStore';

import {DateTimePickerService} from '@app/Services/DateTimePickerService';
import DateTimeService from '@app/Services/DateTimeService';
import {securityService} from '@app/Services/SecurityService';
import {ValidationHelper} from '@app/Services/Validation';

import {Label, TextField, DatePicker, Dropdown, IDropdownOption} from 'office-ui-fabric-react';
import {ExploreLicenseDialog} from '@app/Pages/PersonInfo/Components/Dialogs/ExploreLicenseDialog';
import SelectFilter, {ISelectArguments} from '@app/Components/SelectFilter';
import {TextCell} from '@app/Components/CellFormatters/TextCell';

import {ChangeFieldCallback, ValidateFormCallback} from '@app/Pages/PersonInfo/PersonInfoStore';
import {Loading} from '@app/Components/Loading';
import {CountryModel, PersonInfoModel} from '@app/Models';
import {modalService} from '@app/Components/Modal';
import {AzureAuth} from '@app/Components/AzureAuth';

const CAT_OPT = [
    {key: 'APTL', text: 'APTL'},
    {key: 'CPL', text: 'CPL'},
    {key: 'B', text: 'B'}
];

const MAX_INT: number = 2147483647;
const MAX_DOS_SYMETRIE: number = 9999999;

type LicenseBlockProps = {
    personInfo: PersonInfoModel;
    onChangeFieldHandler: ChangeFieldCallback;
    nationalities: CountryModel[];
    validateFormHandler: ValidateFormCallback;
};

@observer
export class LicenseBlock extends React.Component<LicenseBlockProps, {}> {
    private _store: LicenseBlockStore;

    constructor(props: LicenseBlockProps) {
        super(props);
        this._store = new LicenseBlockStore(props.personInfo.id);
    }

    componentDidUpdate(prevProps: Readonly<LicenseBlockProps>) {
        if (prevProps.personInfo.id !== this.props.personInfo.id) {
            this._store.setPersonId(this.props.personInfo.id);
            this._store.loadPersonLicense(this._store.idPerson);
        }
    }

    render() {
        const {personInfo} = this.props;
        if (!personInfo) return;
        const {hasPermission, permissionCss} = securityService;
        return (
            <fieldset className="person-info-license">
                <legend>License</legend>
                <div className="row">
                    {hasPermission(Permissions.ViewPersonExpireDateMedical) &&
                    <div className={'col-4' + permissionCss(Permissions.ViewPersonExpireDateMedical, Permissions.EditPersonExpireDateMedical)}>
                        <DatePicker
                            className="date-field"
                            label="Expire date medical"
                            allowTextInput={hasPermission(Permissions.EditPersonExpireDateMedical, !AzureAuth.isReadOnly)}
                            calloutProps={{hidden: !hasPermission(Permissions.EditPersonExpireDateMedical, !AzureAuth.isReadOnly)}}
                            formatDate={DateTimePickerService.onFormatDate}
                            parseDateFromString={DateTimePickerService.onParseDateFromString}
                            onSelectDate={this._onSelectMedicalDate}
                            value={personInfo.medicalCheckValidTillDate || undefined}
                            disabled={!hasPermission(Permissions.EditPersonExpireDateMedical, !AzureAuth.isReadOnly)}
                        />
                    </div>
                    }
                    {hasPermission(Permissions.ViewPersonSeniorityBase) &&
                    <div className={'col-4' + permissionCss(Permissions.ViewPersonSeniorityBase, Permissions.EditPersonSeniorityBase)}>
                        <DatePicker
                            className="date-field"
                            label="Planning Seniority Base (last Entry)"
                            allowTextInput={false}
                            calloutProps={{hidden: true}}
                            formatDate={DateTimePickerService.onFormatDate}
                            parseDateFromString={DateTimePickerService.onParseDateFromString}
                            value={personInfo.seniorityBaseDate || undefined}
                            disabled={true}
                        />
                    </div>
                    }
                </div>
                <div className="row">
                   {hasPermission(Permissions.ViewPersonFlightLicenseNumber) &&
                    <div className={'col-4' + permissionCss(Permissions.ViewPersonFlightLicenseNumber)}>
                        <TextField
                            label="Flight License Number"
                            maxLength={20}
                            value={personInfo.flightLicenseNumber || ''}
                            disabled={true}
                        />
                    </div> || <div className="col-4"/>
                    }
                    {hasPermission(Permissions.ViewPersonSeniorityOverride) &&
                    <div className={'col-4' + permissionCss(Permissions.ViewPersonSeniorityOverride, Permissions.EditPersonSeniorityOverride)}>
                        <DatePicker
                            className="date-field"
                            label="Planning Seniority Override"
                            allowTextInput={hasPermission(Permissions.EditPersonSeniorityOverride, !AzureAuth.isReadOnly)}
                            calloutProps={{hidden: !hasPermission(Permissions.EditPersonSeniorityOverride, !AzureAuth.isReadOnly)}}
                            formatDate={DateTimePickerService.onFormatDate}
                            parseDateFromString={DateTimePickerService.onParseDateFromString}
                            onSelectDate={this._onSelectSeniorityOverrideDate}
                            value={personInfo.seniorityOverrideDate || undefined}
                            disabled={!hasPermission(Permissions.EditPersonSeniorityOverride, !AzureAuth.isReadOnly)}
                        />
                    </div>
                    }

                </div>
                <div className="row">
                    <div className="col-4"/>
                    {hasPermission(Permissions.ViewPersonSeniorityComment) &&
                    <div className={'col-4' + permissionCss(Permissions.ViewPersonSeniorityComment, Permissions.EditPersonSeniorityComment)}>
                        <TextField
                            multiline
                            label="Planning Seniority Comment"
                            maxLength={256}
                            onChange={this._onChangeSeniorityOverrideComment}
                            value={personInfo.seniorityOverrideComment || ''}
                            disabled={!hasPermission(Permissions.EditPersonSeniorityComment, !AzureAuth.isReadOnly)}
                        />
                    </div>
                    }
                </div>
                <div className="row">
                    {hasPermission(Permissions.ViewLicensesOverview) &&
                    <div className="col-8">
                        <Loading loading={this._store.loader.isPending}/>
                        <div className="table-label" style={{display: 'flex', justifyContent: 'space-between'}}>
                            <Label>Work Licenses </Label>
                            <Label>show all licenses <Input addon type="checkbox" style={{verticalAlign: 'middle'}} checked={this._store.showAllLicenses} onChange={this._store.switchLicenses}/></Label>
                        </div>
                        {this._store.personLicense && <ReactTable
                            data={this._store.personLicense}
                            columns={this._licenceColumns}
                            pageSize={this._store.personLicense.length}
                            showPageSizeOptions={false}
                            getTrProps={this._setRowColor}
                            showPagination={false}
                            className={'ReactTable-fixed-height'}
                        />}
                    </div>
                    }
                    <div className="col-4">
                        {hasPermission(Permissions.ViewPersonDosimetryTotal) &&
                        <div className={permissionCss(Permissions.ViewPersonDosimetryTotal, Permissions.EditPersonDosimetryTotal)}>
                            <TextField
                                label="Dosimetry before Edelweiss (Total, mSv)"
                                maxLength={11}
                                onChange={this._onChangeTotalDosSymetrieBeforeEdw}
                                value={personInfo.totalDosSymetrieBeforeEdw ? personInfo.totalDosSymetrieBeforeEdw.toString() : '0'}
                                onGetErrorMessage={this.getTotalDosSymetrieErrorMessage}
                                onKeyPress={ValidationHelper.validateFloat}
                                disabled={!hasPermission(Permissions.EditPersonDosimetryTotal, !AzureAuth.isReadOnly)}
                            />
                        </div>
                        }
                        {hasPermission(Permissions.ViewPersonDosimetryYTD) &&
                        <div className={permissionCss(Permissions.ViewPersonDosimetryYTD, Permissions.EditPersonDosimetryYTD)}>
                            <TextField
                                label="Dosimetry before Edelweiss (YTD, mSv)"
                                maxLength={11}
                                onChange={this._onChangeYearToDateDosSymetrieBeforeEdw}
                                value={personInfo.yearToDateDosSymetrieBeforeEdw ? personInfo.yearToDateDosSymetrieBeforeEdw.toString() : '0'}
                                onGetErrorMessage={this.getYearDosSymetrieErrorMessage}
                                onKeyPress={ValidationHelper.validateFloat}
                                disabled={!hasPermission(Permissions.EditPersonDosimetryYTD, !AzureAuth.isReadOnly)}
                            />
                        </div>
                        }
                        {hasPermission(Permissions.ViewPersonSettingsFlightHoursBeforeEdelweiss) &&
                        <div className={permissionCss(Permissions.ViewPersonSettingsFlightHoursBeforeEdelweiss, Permissions.EditPersonFlightHoursBeforeEdelweiss)}>
                            <TextField
                                label="Flight-hours before Edelweiss"
                                onKeyPress={ValidationHelper.validateNumber}
                                maxLength={10}
                                onGetErrorMessage={this.getFlightHoursBeforeEdwErrorMessage}
                                onChange={this._onChangeFlightHoursBeforeEdw}
                                value={personInfo.flightHoursBeforeEdw && personInfo.flightHoursBeforeEdw.toString() || ''}
                                disabled={!hasPermission(Permissions.EditPersonFlightHoursBeforeEdelweiss, !AzureAuth.isReadOnly)}
                            />
                        </div>
                        }
                    </div>
                </div>
            </fieldset>
        );
    }

    @action.bound
    private _onChangeSeniorityOverrideComment(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) {
        this.props.onChangeFieldHandler('seniorityOverrideComment', newValue);
    }

    @action.bound
    private _onChangeTotalDosSymetrieBeforeEdw(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) {
        const value = newValue && parseFloat(newValue) ? Number(parseFloat(newValue).toFixed(3)) : 0;
        this.props.onChangeFieldHandler('totalDosSymetrieBeforeEdw', value);
    }

    @action.bound
    private _onChangeYearToDateDosSymetrieBeforeEdw(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) {
        const value = newValue && parseFloat(newValue) ? Number(parseFloat(newValue).toFixed(3)) : 0;
        this.props.onChangeFieldHandler('yearToDateDosSymetrieBeforeEdw', value);
    }

    @action.bound
    private _onChangeFlightHoursBeforeEdw(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) {
        const value = newValue ? parseInt(newValue) : 0;
        this.props.onChangeFieldHandler('flightHoursBeforeEdw', value);
    }

    @action.bound
    getErrorMessage(value: string): string {
        const isValid = !value || iso.is3(value.toUpperCase());
        this.props.validateFormHandler('flightLicenseCountry', isValid);
        return isValid ? '' : 'Country code should be ISO 3166-1 alpha 3 format';
    }

    @action.bound
    getFlightHoursBeforeEdwErrorMessage(value: string): string {
        const isValid = !value || !(parseInt(value) > MAX_INT);
        this.props.validateFormHandler('flightHoursBeforeEdw', isValid);
        return isValid ? '' : ('Value is too big for this field. Max value is ' + MAX_INT);
    }

    @action.bound
    getTotalDosSymetrieErrorMessage(value: string): string {
        const isValid = !value || !(parseFloat(value) > MAX_DOS_SYMETRIE);
        this.props.validateFormHandler('totalDosSymetrieBeforeEdw', isValid);
        return isValid ? '' : ('Value is too big for this field. Max value is ' + MAX_DOS_SYMETRIE);
    }

    @action.bound
    getYearDosSymetrieErrorMessage(value: string): string {
        const isValid = !value || !(parseFloat(value) > MAX_DOS_SYMETRIE);
        this.props.validateFormHandler('yearToDateDosSymetrieBeforeEdw', isValid);
        return isValid ? '' : ('Value is too big for this field. Max value is ' + MAX_DOS_SYMETRIE);
    }

    @action.bound
    private _onSelectSeniorityOverrideDate(date: Date | null | undefined): void {
        const value = date ? DateTimeService.date(date.getFullYear(), date.getMonth(), date.getDate()) : undefined;
        this.props.onChangeFieldHandler('seniorityOverrideDate', value);
    }

    @action.bound
    private _onSelectMedicalDate(date: Date | null | undefined): void {
        const value = date ? DateTimeService.date(date.getFullYear(), date.getMonth(), date.getDate()) : undefined;
        this.props.onChangeFieldHandler('medicalCheckValidTillDate', value);
    }

    private _renderExploreLicenseInactivationsColumn = (cellInfo: CellInfo) => {
        return <i className="ms-Icon ms-Icon-action ms-Icon--RedEye" aria-hidden="true" onClick={() => this._onExploreLicenseInactivationsClick(cellInfo)}/>;
    };

    @action.bound
    private async _onExploreLicenseInactivationsClick(cellInfo: CellInfo) {
        await modalService.show(ExploreLicenseDialog, {
            personLicense: cellInfo.row._original,
            personId: this.props.personInfo.id,
            onChangeLicenseInactivationHandler: this.onChangeLicenseInactivationCallback
        });
    }

    @action.bound
    private onChangeLicenseInactivationCallback(personId: string) {
        this._store.loadPersonLicense(personId);
    }

    private _renderCheckBoxColumn = (cellInfo: CellInfo) => {
        if (cellInfo.value > 0) {
            return <i className="ms-Icon ms-Icon-action ms-Icon--Accept" aria-hidden="true"/>;
        }
        return <React.Fragment/>;
    };

    private _renderLicenseDate = (cellInfo: CellInfo) => {
        return <TextCell value={cellInfo.value ? DateTimeService.toUiDate(cellInfo.value) : ''}/>;
    };

    private _renderLicense = (cellInfo: CellInfo) => {
        return <TextCell value={cellInfo.value}/>;
    };

    private _setRowColor = (_: unknown, rowInfo: RowInfo | undefined) => {
        let conf = {};
        if (rowInfo && rowInfo.row && rowInfo.row.planningLicenseInactivationCount > 0) {
            conf = {style: {backgroundColor: 'rgba(255,165,0,0.5)'}};
        }
        return conf;
    };

    private _licenceColumns: Column[] = [
        {
            Header: 'License',
            accessor: 'license',
            Cell: this._renderLicense,
            className: securityService.permissionCss(Permissions.ViewLicensesOverview)
        },
        {
            Header: 'Date From',
            accessor: 'dateFrom',
            Cell: this._renderLicenseDate,
            className: securityService.permissionCss(Permissions.ViewLicensesOverview)
        },
        {
            Header: 'Date To',
            accessor: 'dateTo',
            Cell: this._renderLicenseDate,
            className: securityService.permissionCss(Permissions.ViewLicensesOverview)
        },
        {
            Header: 'Deactivation',
            accessor: 'deactivationDate',
            Cell: this._renderLicenseDate,
            className: securityService.permissionCss(Permissions.ViewLicensesOverview)
        },
        {
            Header: 'Reactivation',
            accessor: 'reactivationDate',
            Cell: this._renderLicenseDate,
            className: securityService.permissionCss(Permissions.ViewLicensesOverview)
        },
        {
            Header: 'Has Overrides',
            accessor: 'planningLicenseInactivationCount',
            Cell: this._renderCheckBoxColumn,
            maxWidth: 100,
            className: securityService.permissionCss(Permissions.ViewLicensesOverview)
        },
        {
            Header: '',
            accessor: 'id',
            Cell: this._renderExploreLicenseInactivationsColumn,
            maxWidth: 60,
            show: securityService.hasPermission(Permissions.ViewObjectPersonLicenseInactivation),
            className: securityService.permissionCss(Permissions.ViewObjectPersonLicenseInactivation, Permissions.EditObjectPersonLicenseInactivation)
        }
    ];
}